/**
 * DataAdminTools.jsx — Iter 139.4 (2026-05-26)
 * ──────────────────────────────────────────────
 * Two super_admin-only utilities surfaced from /settings → Data & Storage:
 *
 *   1) "Backfill Last Contact Date" — runs
 *      `POST /api/admin/backfill/last-contact-date` which walks
 *      activities, activity_log, dispositions, and rc_call_sessions
 *      to retro-stamp `last_contact_date` on leads contacted before
 *      the iter-139.2 backend fix shipped. Idempotent + safe to re-run.
 *      Fixes ioannis@ehiehr.com ticket #bae1e114.
 *
 *   2) "Find duplicate leads" — surfaces dupe groups detected via
 *      `GET /api/admin/duplicate-leads` (grouped on normalised
 *      company + contact_name). Each group shows the dupes side-by-side
 *      so the admin can pick a primary + click Merge → calls
 *      `POST /api/admin/duplicate-leads/merge` which soft-deletes the
 *      losers into `archived_leads` and re-targets their activity_log
 *      rows to the primary. Fixes ioannis ticket #2b7df372.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Users } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function DataAdminTools({ token }) {
  const hdrs = { Authorization: `Bearer ${token || (typeof localStorage !== "undefined" && localStorage.getItem("token")) || ""}` };
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [dupeBusy, setDupeBusy] = useState(false);
  const [dupeGroups, setDupeGroups] = useState(null); // null = not run, [] = none, [...] = found
  const [primaryByGroup, setPrimaryByGroup] = useState({}); // {groupIdx: leadId}
  const [mergingIdx, setMergingIdx] = useState(null);

  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await axios.post(`${API}/admin/backfill/last-contact-date`, {}, { headers: hdrs });
      setBackfillResult(r.data);
      toast.success(`Stamped last_contact_date on ${r.data.leads_updated} leads`);
    } catch (e) {
      toast.error(`Backfill failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  const findDupes = async () => {
    setDupeBusy(true);
    try {
      const r = await axios.get(`${API}/admin/duplicate-leads`, { headers: hdrs });
      const groups = r.data?.groups || [];
      setDupeGroups(groups);
      // Pre-pick the oldest lead in each group as the suggested primary
      // (preserves the longest contact history).
      const picks = {};
      groups.forEach((g, idx) => {
        const sorted = [...g.leads].sort((a, b) => (a.created_at || "") > (b.created_at || "") ? 1 : -1);
        picks[idx] = sorted[0]?.id;
      });
      setPrimaryByGroup(picks);
      toast.success(`Found ${groups.length} duplicate group${groups.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(`Detection failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDupeBusy(false);
    }
  };

  const mergeGroup = async (idx) => {
    const group = dupeGroups[idx];
    const primary_id = primaryByGroup[idx];
    if (!primary_id) { toast.error("Pick a primary lead first"); return; }
    const merge_ids = group.leads.map((l) => l.id).filter((id) => id !== primary_id);
    if (!merge_ids.length) { toast.info("Nothing to merge — only one lead in this group"); return; }
    setMergingIdx(idx);
    try {
      const r = await axios.post(`${API}/admin/duplicate-leads/merge`, { primary_id, merge_ids }, { headers: hdrs });
      toast.success(`Merged ${r.data.archived_count} dupes into "${group.company}"`);
      // Drop the group from the local list
      setDupeGroups((arr) => arr.filter((_, i) => i !== idx));
    } catch (e) {
      toast.error(`Merge failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setMergingIdx(null);
    }
  };

  return (
    <div data-testid="data-admin-tools" style={{ marginTop: 24, borderTop: "0.5px solid var(--border)", paddingTop: 20 }}>
      <h3 style={{ fontSize: 13, fontWeight: 650, marginBottom: 8 }}>Lead data tools</h3>

      {/* ── 1) Backfill last_contact_date ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "0.5px solid var(--border)" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>Backfill "Last Contact Date"</div>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
            Walks activities, activity_log, dispositions, and RC call logs to retro-stamp leads contacted before the May-26 fix shipped. Idempotent.
          </div>
          {backfillResult && (
            <div data-testid="backfill-result" style={{ fontSize: 11, color: "#0F6E56", marginTop: 6, background: "#DAFBE8", border: "0.5px solid #BCEACB", borderRadius: 6, padding: "6px 10px" }}>
              ✓ Stamped {backfillResult.leads_updated} leads · scanned {backfillResult.leads_with_contact_history} with history
            </div>
          )}
        </div>
        <button
          data-testid="backfill-last-contact-btn"
          onClick={runBackfill}
          disabled={backfilling}
          className="btn btn-primary"
          style={{ fontSize: 12, opacity: backfilling ? 0.6 : 1 }}
        >
          {backfilling ? <><Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Running…</> : "Run backfill"}
        </button>
      </div>

      {/* ── 2) Duplicate detection + merge ── */}
      <div style={{ padding: "16px 0 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>Duplicate leads</div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
              Groups leads with the same normalised <code>company + contact_name</code>. Pick which copy to keep, then merge — losers move to <code>archived_leads</code> with their activity history re-pointed to the primary.
            </div>
          </div>
          <button
            data-testid="find-dupes-btn"
            onClick={findDupes}
            disabled={dupeBusy}
            className="btn"
            style={{ fontSize: 12, opacity: dupeBusy ? 0.6 : 1 }}
          >
            {dupeBusy ? <><Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Scanning…</> : <><Sparkles style={{ width: 12, height: 12 }} /> Find duplicates</>}
          </button>
        </div>

        {Array.isArray(dupeGroups) && dupeGroups.length === 0 && (
          <div style={{ fontSize: 11, color: "var(--text-secondary)", padding: "10px 0" }}>No duplicate groups detected.</div>
        )}

        {Array.isArray(dupeGroups) && dupeGroups.length > 0 && (
          <div data-testid="dupe-group-list">
            {dupeGroups.map((g, idx) => (
              <div key={`${g.company}-${g.contact_name}-${idx}`} data-testid={`dupe-group-${idx}`} style={{ border: "0.5px solid var(--border)", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Users style={{ width: 14, height: 14, color: "var(--blue)" }} />
                  <div style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
                    {g.leads[0]?.company || g.company} · <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>{g.leads[0]?.contact_name || g.contact_name}</span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#A32D2D", background: "#FCEBEB", border: "0.5px solid #F8D5D5", borderRadius: 999, padding: "2px 8px" }}>{g.count} copies</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto", columnGap: 10, rowGap: 6, fontSize: 11, alignItems: "center" }}>
                  <div style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Keep</div>
                  <div style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Identifier</div>
                  <div style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Phone / Email</div>
                  <div style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Last contact</div>
                  {g.leads.map((l) => (
                    <React.Fragment key={l.id}>
                      <input
                        type="radio"
                        name={`primary-${idx}`}
                        data-testid={`dupe-primary-${idx}-${l.id.slice(0, 8)}`}
                        checked={primaryByGroup[idx] === l.id}
                        onChange={() => setPrimaryByGroup((p) => ({ ...p, [idx]: l.id }))}
                      />
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ color: "var(--text-tertiary)", fontFamily: "monospace" }}>{l.id.slice(0, 8)}</span>
                        {l.npi ? <span style={{ marginLeft: 6, color: "var(--text-tertiary)" }}>NPI {l.npi}</span> : null}
                      </div>
                      <div style={{ color: "var(--text-secondary)" }}>{l.phone || l.email || "—"}</div>
                      <div style={{ color: "var(--text-secondary)" }}>{l.last_contact_date ? new Date(l.last_contact_date).toLocaleDateString() : "—"}</div>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <button
                    data-testid={`dupe-merge-btn-${idx}`}
                    onClick={() => mergeGroup(idx)}
                    disabled={mergingIdx === idx || !primaryByGroup[idx]}
                    className="btn btn-primary"
                    style={{ fontSize: 11, opacity: (mergingIdx === idx || !primaryByGroup[idx]) ? 0.6 : 1 }}
                  >
                    {mergingIdx === idx ? <><Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> Merging…</> : `Merge ${g.count - 1} into primary`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
